/**
 * InsufficientFundsToast.tsx — R7 #8
 *
 * Transient 2.5s toast shown when a player clicks Take Over but cannot afford it.
 * "Need {N} tokens to take over — you have {M}."
 * Fires via store `showInsufficientFundsToast` / `openTakeoverConfirm`.
 *
 * VISIBLE WHEN: insufficientFundsToast !== null
 * TEST PATH: land on opponent facility → BuildMenu → click Take Over with < needed tokens
 *   → toast appears top-center → auto-hides ~2.5s or click-to-dismiss.
 */

import type React from "react";
import { useEffect } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./InsufficientFundsToast.module.css";

const TOAST_DURATION_MS = 2500;

export function InsufficientFundsToast(): React.ReactElement | null {
  const { t } = useI18n();
  const toast = useGameStore((s) => s.insufficientFundsToast);
  const dismiss = useGameStore((s) => s.dismissInsufficientFundsToast);

  useEffect(() => {
    if (toast === null) return;
    const timer = window.setTimeout(() => {
      dismiss();
    }, TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [toast, dismiss]);

  if (toast === null) return null;

  return (
    <button
      type="button"
      className={styles.toast}
      onClick={dismiss}
      aria-label="Dismiss insufficient funds notification"
      data-testid="insufficient-funds-toast"
    >
      <div className={styles.title}>{t("toast.insufficientFunds.title")}</div>
      <div className={styles.message}>
        {t("toast.insufficientFunds.body", { needed: toast.needed, have: toast.have })}
      </div>
    </button>
  );
}
